import {
  Avatar,
  Box,
  FloatingPosition,
  Group,
  Loader,
  Menu,
  ScrollAreaAutosize,
  Skeleton,
  Text,
  ThemeIcon,
  useComputedColorScheme,
} from "@mantine/core";
import { IconChevronRight, IconPlus } from "@tabler/icons-react";
import clsx from "clsx";
import { EventName, normalizeText } from "core";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../auth/useAuthContext";
import { useIsGranted } from "../../auth/useIsGranted";
import { useProjects, useSwitchProject } from "../../utils/useProjects";
import { useTrackEvent } from "../../utils/useTrackEvent";
import classes from "./Layout.module.css";

function ProjectSwitcherItem({
  active = false,
  name,
  orgLogo,
  onSelectOrganization,
  rightSection,
}: {
  active?: boolean;
  name?: string;
  orgLogo?: string;
  onSelectOrganization?: () => void;
  rightSection?: React.JSX.Element;
}) {
  const showLogo = !!orgLogo;
  const computedColorScheme = useComputedColorScheme("dark", {
    getInitialValueInEffect: true,
  });
  const activeVariant = computedColorScheme === "dark" ? "light" : "filled";

  return (
    <Group
      flex={1}
      gap="xs"
      onClick={onSelectOrganization}
      w="100%"
      wrap="nowrap"
      className={clsx(active && classes.sidebarOrgSwitcherItemActive)}
    >
      {/* Set the icon color based on org name initials, if looking at the list of orgs */}
      <Avatar
        variant={active ? activeVariant : "default"}
        color={active ? "var(--mantine-primary-color-filled)" : undefined}
        size="sm"
        name={name}
        src={showLogo ? orgLogo : undefined}
        radius={showLogo ? "sm" : "xl"}
        classNames={{
          image: classes.sidebarOrgSwitcherLogo,
        }}
      />
      <Group
        className={classes.sidebarOrgSwitcherItemLabelAction}
        flex={1}
        wrap="nowrap"
        gap={"xs"}
      >
        <Box size="md" flex={1} pl="xs">
          {name ? (
            <Text {...(!active ? { truncate: true, maw: 180 } : {})}>
              {name}
            </Text>
          ) : (
            <Skeleton w={120} h={25} animate />
          )}
        </Box>
        {rightSection}
      </Group>
    </Group>
  );
}

const projectSearchMinCount = 6;

interface ProjectSwitcherProps {
  menuPosition?: FloatingPosition;
  disableCreateProject?: boolean;
  hideIfNoOtherProjects?: boolean;
  switcherTarget?: React.JSX.Element;
}

export function ProjectSwitcher({
  menuPosition = "bottom-start",
  disableCreateProject = false,
  hideIfNoOtherProjects = false,
  switcherTarget,
}: ProjectSwitcherProps) {
  const navigate = useNavigate();
  const { projects } = useProjects();

  const { track } = useTrackEvent();
  const { identity } = useAuthContext();
  const { hasPermission: hasCreateProjectPermission } = useIsGranted({
    permission: "organization.create",
  });

  const { switchProject, isSwitchingProject } = useSwitchProject();
  const [projectSearch, setProjectSearch] = useState("");

  const availableProjects = projects.filter(
    (o) => o.id !== identity.organizationId,
  );
  const activeProject = projects.find((o) => o.id === identity.organizationId);
  const hasMultipleProjects = availableProjects.length > 0;
  const showProjectSearch = availableProjects.length >= projectSearchMinCount;
  const filteredProjects = availableProjects.filter((o) =>
    normalizeText(o.name).includes(normalizeText(projectSearch)),
  );
  const canCreateProject = hasCreateProjectPermission && !disableCreateProject;
  const isSwitcherEnabled = hasMultipleProjects || canCreateProject;

  function onClickAddProject() {
    track(EventName.ClickedNavAddProjectButton);
    navigate("/account/projects");
  }

  if (hideIfNoOtherProjects && !hasMultipleProjects) return null;

  return (
    <Menu
      position={menuPosition}
      offset={3}
      width={380}
      disabled={!isSwitcherEnabled || isSwitchingProject}
      withArrow
      arrowPosition="side"
      arrowOffset={30}
      arrowSize={10}
    >
      <Menu.Target>
        {switcherTarget ?? (
          <Group
            w="100%"
            px="sm"
            py="xs"
            align="center"
            component={"button"}
            justify="flex-start"
            className={clsx([
              classes.sidebarOrgSwitcherButton,
              isSwitcherEnabled && classes.sidebarOrgSwitcherButtonEnabled,
            ])}
          >
            <ProjectSwitcherItem
              active
              name={activeProject?.name}
              rightSection={
                isSwitcherEnabled ? (
                  <ThemeIcon variant="transparent" color="dimmed" size="sm">
                    {isSwitchingProject ? (
                      <Loader size="sm" color="dimmed" />
                    ) : (
                      <IconChevronRight />
                    )}
                  </ThemeIcon>
                ) : undefined
              }
            />
          </Group>
        )}
      </Menu.Target>

      <Menu.Dropdown>
        {showProjectSearch && (
          <Menu.Search
            placeholder="Search projects"
            value={projectSearch}
            onChange={(event) => setProjectSearch(event.currentTarget.value)}
          />
        )}
        <ScrollAreaAutosize
          mah={200}
          type="hover"
          scrollbars="y"
          scrollbarSize={4}
          offsetScrollbars
        >
          {canCreateProject && (
            <>
              <Menu.Item py={4} onClick={onClickAddProject}>
                <Group gap={2} justify="flex-end">
                  <ThemeIcon
                    className={classes.sidebarSwitcherAddProjectButton}
                    variant="transparent"
                  >
                    <IconPlus size={14} stroke={3} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed" fw="bold">
                    Add project
                  </Text>
                </Group>
              </Menu.Item>
              {hasMultipleProjects && <Menu.Divider />}
            </>
          )}
          {hasMultipleProjects && filteredProjects.length === 0 && (
            <Text size="xs" c="dimmed" px="sm" py="xs">
              No project matches
            </Text>
          )}
          {filteredProjects.map((org) => (
            <Menu.Item key={org.id}>
              <ProjectSwitcherItem
                name={org.name}
                orgLogo={org.logoUrl}
                onSelectOrganization={() => {
                  switchProject({ id: org.id });
                }}
                rightSection={
                  <Text size="xs" c="dimmed">
                    Switch project
                  </Text>
                }
              />
            </Menu.Item>
          ))}
        </ScrollAreaAutosize>
      </Menu.Dropdown>
    </Menu>
  );
}
